import { createSelector } from '@reduxjs/toolkit';
import {
    FileSearch,
    FolderOpen,
    Info,
    Loader2,
    MessageSquare,
    MessageSquarePlus,
    Search,
    X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_JURISDICTION, JurisdictionCode } from '../../common/jurisdictions';
import { useVirtualList } from '../../hooks/useVirtualList';
import { getLanguageDirection } from '../../i18n/config';
import { Chat } from '../../models/chat';
import { createFilteredListSelector, createTextSearchFilter } from '../../redux/selectors/listSelectors';
import { setCurrentChatId } from '../../redux/slices/chatSlice';
import { toggleShowConversations, toggleShowDocumentSearches } from '../../redux/slices/uiSlice';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { caseSelectOneObjectByQuery, chatSelectByQuery, DBCaseThunks, DBChatThunks, DBMessageThunks, userSelectOneObjectByQuery } from '../../redux/thunks/DBServiceThunks';
import CreateChatDialog from '../common/CreateChatDialog';
import JurisdictionSelect from '../common/JurisdictionSelect';
import ChatItem from './chats/ChatItem';
import DocumentSearchModal from '../document-search/DocumentSearchModal';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import feathersClient from '../../feathers';
import { getLanguageByCode } from '../../i18n/languages';
import { selectCanEditCurrentCase } from '../../redux/selectors/knowledgebaseSelectors';

// Memoized selectors
const selectCurrentCase = createSelector(
    [(state) => state.DBCaseReducer, (state) => state.CaseReducer.currentCaseId],
    (caseReducer, currentCaseId) => caseSelectOneObjectByQuery(caseReducer, { _id: currentCaseId })
);

const selectChats = createSelector(
    [(state) => state.DBChatReducer, (state) => state.CaseReducer.currentCaseId],
    (chatReducer, currentCaseId) => chatSelectByQuery(chatReducer, { caseId: currentCaseId })
);

const selectFilteredChats = createFilteredListSelector(
    selectChats,
    createTextSearchFilter((chat: Chat) => chat.title)
);

const ChatListView: React.FC = () => {
    const { t, i18n } = useTranslation();
    const isRTL = getLanguageDirection(i18n.language);
    const dispatch = useAppDispatch();

    const currentCaseId = useAppSelector(state => state.CaseReducer.currentCaseId);
    const currentCase = useAppSelector(selectCurrentCase);
    const isLoading = useAppSelector(state => state.DBChatReducer.itemsLoading);
    const currentUser = useAppSelector(state => userSelectOneObjectByQuery(state.DBUserReducer, {}));
    const showConversations = useAppSelector(state => state.UIReducer.showConversations);
    const showDocumentSearches = useAppSelector(state => state.UIReducer.showDocumentSearches);

    const [searchTerm, setSearchTerm] = useState('');

    const allFilteredChats = useAppSelector(state => selectFilteredChats(state, searchTerm));

    const filteredChats = React.useMemo(() => {
        return allFilteredChats.filter(chat => {
            const chatType = chat.type || 'conversation';
            if (chatType === 'conversation' && !showConversations) return false;
            if (chatType === 'documentSearch' && !showDocumentSearches) return false;
            return true;
        });
    }, [allFilteredChats, showConversations, showDocumentSearches]);

    const hasActiveFilters = searchTerm || !showConversations || !showDocumentSearches;

    const CARD_HEIGHT = 88;
    const GAP = 12;
    const ITEM_HEIGHT = CARD_HEIGHT + GAP;
    const CONTAINER_HEIGHT = 400;
    const BOTTOM_PADDING = 16;

    const {
        scrollElementRef,
        visibleItems: visibleChats,
        totalHeight: baseHeight,
        offsetY,
        handleScroll
    } = useVirtualList(filteredChats, ITEM_HEIGHT, CONTAINER_HEIGHT);

    const canEdit = useAppSelector(selectCanEditCurrentCase);

    const totalHeight = filteredChats.length > 0
        ? baseHeight + BOTTOM_PADDING - GAP
        : baseHeight;

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [documentSearchModalOpen, setDocumentSearchModalOpen] = useState(false);

    const handleCreateNewChat = () => setCreateDialogOpen(true);
    const handleCloseCreateDialog = () => setCreateDialogOpen(false);

    const handleCreateChat = async (title: string) => {
        const newChat: Chat = { title, caseId: currentCaseId };
        const result = await dispatch(DBChatThunks.create(newChat));
        if (result.payload) {
            dispatch(setCurrentChatId((result.payload as Chat)._id));
        }
    };

    const handleOpenDocumentSearch = () => setDocumentSearchModalOpen(true);

    const handleDocumentSearchSubmit = async (data: {
        query: string;
        selectedKnowledgebaseIds: string[];
        jurisdiction: JurisdictionCode;
        chatId: string;
    }) => {
        try {
            const queryId = `query_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

            const userLanguageCode = currentUser?.language || 'en';
            const userLanguage = getLanguageByCode(userLanguageCode);
            const languageCode = userLanguage.code;

            await feathersClient.service('actions').create({
                cmd: 'document-search',
                queryText: data.query,
                selectedKnowledgebaseIds: data.selectedKnowledgebaseIds,
                caseId: currentCaseId,
                chatId: data.chatId,
                jurisdiction: data.jurisdiction,
                language: languageCode,
                queryId: queryId
            });

            dispatch(setCurrentChatId(data.chatId));
            await dispatch(DBChatThunks.find({ caseId: currentCaseId, $paginate: false })).unwrap();

            const checkForAssistantMessage = async (retries = 0, maxRetries = 10) => {
                if (retries >= maxRetries) {
                    console.warn(`[DocumentSearch] ❌ Max retries reached, giving up on detecting assistant message`);
                    return;
                }
                console.log(`[DocumentSearch] 🔍 Checking for assistant message (attempt ${retries + 1}/${maxRetries})`);

                try {
                    const result = await dispatch(DBMessageThunks.find({
                        chatId: data.chatId,
                        sender: 'assistant',
                        'metadata.queryId': queryId,
                        $paginate: false
                    })).unwrap();

                    const messages = Array.isArray(result) ? result : result.data || [];
                    const assistantMessage = messages.find((msg: any) => msg.metadata?.queryId === queryId);

                    if (assistantMessage) {
                        console.log(`✅ [DocumentSearch] Found assistant message with queryId: ${queryId}`);
                        await dispatch(DBMessageThunks.find({ chatId: data.chatId, $paginate: false })).unwrap();
                        console.log(`✅ [DocumentSearch] Messages fetched, DocumentSearchView should render now!`);
                    } else {
                        console.log(`⏳ [DocumentSearch] Assistant message not found yet, retrying in 3s...`);
                        setTimeout(() => checkForAssistantMessage(retries + 1, maxRetries), 3000);
                    }
                } catch (error) {
                    console.error(`❌ [DocumentSearch] Error checking for assistant message:`, error);
                    setTimeout(() => checkForAssistantMessage(retries + 1, maxRetries), 3000);
                }
            };

            setTimeout(() => checkForAssistantMessage(), 3000);
        } catch (error) {
            console.error('Error submitting document search:', error);
        }
    };

    useEffect(() => {
        if (currentCaseId) {
            dispatch(DBChatThunks.find({ caseId: currentCaseId, $paginate: false }));
        }
    }, [currentCaseId, dispatch]);

    if (!currentCaseId) {
        return (
            <div className="flex flex-col h-full justify-center items-center p-6">
                <div className="flex flex-col items-center py-10 px-8 rounded-2xl border border-dashed border-border bg-card/70 text-center max-w-[400px] animate-in fade-in duration-500">
                    <div className="p-3 rounded-full bg-gold/10 mb-5">
                        <FolderOpen size={36} className="text-gold" />
                    </div>
                    <h6 className="text-base font-semibold text-foreground mb-1">
                        {t('common.chat.noCaseSelected')}
                    </h6>
                    <p className="text-sm text-muted-foreground">
                        {t('common.chat.selectCaseToView')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div dir={isRTL} className="flex flex-col h-full">

            {/* Header */}
            <div className="px-4 py-4 flex flex-col gap-2 border-b border-border/70 min-h-[88px] justify-center">
                <h2 className="text-lg font-semibold truncate">{currentCase?.title}</h2>
                {currentCase && (
                    <JurisdictionSelect
                        value={((currentCase.jurisdiction as JurisdictionCode) || (currentUser?.jurisdiction as JurisdictionCode) || DEFAULT_JURISDICTION)}
                        onChange={(code) => {
                            if (currentCaseId) {
                                dispatch(DBCaseThunks.patch({ entity: { _id: currentCaseId, jurisdiction: code }, options: {} as any } as any));
                            }
                        }}
                        label={t('common.jurisdiction.case')}
                        size="small"
                        inline={true}
                        fullWidth={true}
                    />
                )}
            </div>

            {/* Search box */}
            <div className="mb-2">
                <div className="flex items-center rounded-[10px] border border-border/80 bg-card px-2 py-1 hover:border-gold/30 focus-within:border-gold focus-within:ring-2 focus-within:ring-gold/20 transition-all">
                    <Search size={16} className="text-muted-foreground ml-1 mr-2 shrink-0" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('components.chats.searchPlaceholder')}
                        className="flex-1 bg-transparent border-none outline-none text-sm text-foreground placeholder:text-muted-foreground"
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                        >
                            <X size={14} />
                        </button>
                    )}
                </div>

                {/* Type filters */}
                <div className="mt-2 flex gap-4 px-0.5 justify-between">
                    <div className="flex items-center gap-1.5">
                        <Checkbox
                            id="show-conversations"
                            checked={showConversations}
                            onCheckedChange={() => dispatch(toggleShowConversations())}
                        />
                        <Label htmlFor="show-conversations" className="text-sm font-medium cursor-pointer">
                            {t('components.documentSearch.filterConversations')}
                        </Label>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Checkbox
                            id="show-document-searches"
                            checked={showDocumentSearches}
                            onCheckedChange={() => dispatch(toggleShowDocumentSearches())}
                        />
                        <Label htmlFor="show-document-searches" className="text-sm font-medium cursor-pointer">
                            {t('components.documentSearch.filterDocumentSearches')}
                        </Label>
                    </div>
                </div>
            </div>

            {/* Chat list */}
            <div className="flex-1 overflow-hidden flex flex-col">
                {/* Loading */}
                {isLoading && (
                    <div className="flex flex-col items-center justify-center h-full p-6 animate-in fade-in duration-500">
                        <Loader2 size={36} className="text-gold animate-spin mb-3" />
                        <p className="text-sm font-medium text-muted-foreground text-center">
                            {t('common.chat.loadingConversations')}
                        </p>
                    </div>
                )}

                {/* Populated list with virtual scrolling */}
                {!isLoading && filteredChats.length > 0 && (
                    <div
                        ref={scrollElementRef}
                        onScroll={handleScroll}
                        className="flex-1 overflow-auto relative"
                    >
                        <div style={{ height: totalHeight, position: 'relative', paddingBottom: 8 }}>
                            <div
                                style={{ position: 'absolute', top: offsetY, left: 0, right: 0 }}
                                className="flex flex-col gap-3 pb-2"
                            >
                                {visibleChats.map((chat) => (
                                    <ChatItem key={chat._id} chatId={chat._id} />
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Empty state */}
                {!isLoading && filteredChats.length === 0 && (
                    <div className="p-6 text-center rounded-xl border border-dashed border-gold/20 bg-background/50">
                        <MessageSquare size={36} className="text-muted-foreground/40 mx-auto mb-3" />
                        <p className="text-sm text-muted-foreground mb-2">
                            {hasActiveFilters ? t('common.chat.noMatchingConversations') : t('common.chat.noConversationsYet')}
                        </p>
                        {hasActiveFilters && (
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    if (!showConversations) dispatch(toggleShowConversations());
                                    if (!showDocumentSearches) dispatch(toggleShowDocumentSearches());
                                }}
                                className="mt-1 border border-gold/50 text-gold bg-transparent px-3 py-1 rounded-md text-xs font-medium hover:bg-gold/[0.05] hover:border-gold transition-all"
                            >
                                {t('common.chat.clearSearch')}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* CTA Buttons */}
            <TooltipProvider>
                <div className="mt-4 flex flex-col gap-3">
                    {/* New Deep Research */}
                    <div className="relative">
                        <button
                            onClick={handleCreateNewChat}
                            disabled={!canEdit}
                            className={`w-full flex items-center justify-center gap-2 py-[14px] px-4 rounded-xl text-sm font-semibold transition-all duration-300 pr-10 ${
                                canEdit
                                    ? 'bg-gold text-white shadow-[0_4px_14px_rgba(160,122,44,0.4)] hover:bg-gold-bright hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(160,122,44,0.5)] active:translate-y-0'
                                    : 'bg-secondary text-muted-foreground cursor-not-allowed'
                            }`}
                        >
                            <MessageSquarePlus size={18} />
                            <span>{t('common.chat.newDeepResearch')}</span>
                        </button>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white cursor-default z-10"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Info size={15} />
                                </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-center">
                                {t('components.sidebar.newDeepResearchTooltip')}
                            </TooltipContent>
                        </Tooltip>
                    </div>

                    {/* New Precedent & Legislation */}
                    <div className="relative">
                        <button
                            onClick={handleOpenDocumentSearch}
                            disabled={!canEdit}
                            className={`w-full flex items-center justify-center gap-2 py-[14px] px-4 rounded-xl text-sm font-semibold transition-all duration-300 pr-10 ${
                                canEdit
                                    ? 'border-2 border-gold text-gold bg-transparent hover:bg-gold/[0.08] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(160,122,44,0.2)] active:translate-y-0'
                                    : 'border-2 border-border text-muted-foreground cursor-not-allowed'
                            }`}
                        >
                            <FileSearch size={18} />
                            <span>{t('components.sidebar.newPrecedentAndLegislation')}</span>
                        </button>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span
                                    className={`absolute right-3 top-1/2 -translate-y-1/2 cursor-default z-10 ${canEdit ? 'text-gold/60 hover:text-gold' : 'text-muted-foreground/40'}`}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Info size={15} />
                                </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs text-center">
                                {t('components.sidebar.newPrecedentAndLegislationTooltip')}
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </div>
            </TooltipProvider>

            <CreateChatDialog
                open={createDialogOpen}
                onClose={handleCloseCreateDialog}
                onCreateChat={handleCreateChat}
                caseId={currentCaseId}
            />

            <DocumentSearchModal
                isOpen={documentSearchModalOpen}
                onClose={() => setDocumentSearchModalOpen(false)}
                caseId={currentCaseId}
                onSearchSubmit={handleDocumentSearchSubmit}
            />
        </div>
    );
};

export default ChatListView;
